// src/components/admin/OrderManagement.js
import { useState } from 'react';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Package, ChevronDown, ChevronUp, Truck, Search, Filter, Loader2 } from 'lucide-react';
import { ORDER_STATUSES } from '../../config/constants';

export function OrderManagement({ orders, loading }) {
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updating, setUpdating] = useState(null);

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.companyName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleStatusUpdate = async (orderId, newStatus) => {
    setUpdating(orderId);
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        status: newStatus,
        updatedAt: Timestamp.now(),
        ...(newStatus === 'Shipped' && { shippedAt: Timestamp.now() }),
        ...(newStatus === 'Delivered' && { deliveredAt: Timestamp.now() })
      });
    } catch (err) {
      console.error('Error updating order status:', err);
      alert('Failed to update order status');
    } finally {
      setUpdating(null);
    }
  };

  const handleTrackingUpdate = async (orderId, trackingNumber) => {
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        trackingNumber,
        updatedAt: Timestamp.now()
      });
    } catch (err) {
      console.error('Error updating tracking:', err);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-gray-300 border-t-black rounded-full mx-auto"></div>
        <p className="text-gray-500 mt-4">Loading orders...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Order Management</h2>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by order ID, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="all">All Statuses</option>
              {ORDER_STATUSES.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Package size={48} className="text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600">No orders found</h3>
          <p className="text-gray-500">
            {searchTerm || statusFilter !== 'all' 
              ? 'Try adjusting your filters' 
              : 'Orders will appear here when placed'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map(order => {
            const isExpanded = expandedOrder === order.id;
            const orderDate = order.createdAt?.toDate?.() || new Date(order.createdAt);

            return (
              <div key={order.id} className="bg-white rounded-lg shadow overflow-hidden">
                {/* Order Header */}
                <div
                  className="p-4 flex flex-col md:flex-row md:items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                >
                  <div className="flex items-center gap-4 mb-2 md:mb-0">
                    <div>
                      <p className="font-semibold">#{order.id.slice(-8).toUpperCase()}</p>
                      <p className="text-sm text-gray-500">{order.userEmail}</p>
                      {order.companyName && (
                        <p className="text-xs text-gray-400">{order.companyName}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold">${(order.totalWithFee || order.total || 0).toFixed(2)}</p>
                      <p className="text-xs text-gray-500">
                        {orderDate.toLocaleDateString()}
                      </p>
                    </div>

                    {/* Status Dropdown - Stop propagation */}
                    <div onClick={(e) => e.stopPropagation()}>
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusUpdate(order.id, e.target.value)}
                        disabled={updating === order.id}
                        className={`px-3 py-1 rounded-full text-sm font-medium border-0 cursor-pointer
                          ${order.status === 'New' ? 'bg-blue-100 text-blue-800' : ''}
                          ${order.status === 'Processing' ? 'bg-yellow-100 text-yellow-800' : ''}
                          ${order.status === 'Filled' ? 'bg-purple-100 text-purple-800' : ''}
                          ${order.status === 'Shipped' ? 'bg-indigo-100 text-indigo-800' : ''}
                          ${order.status === 'Delivered' ? 'bg-green-100 text-green-800' : ''}
                          ${order.status === 'Cancelled' ? 'bg-red-100 text-red-800' : ''}
                        `}
                      >
                        {ORDER_STATUSES.map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>

                    {updating === order.id && <Loader2 size={16} className="animate-spin" />}
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Items */}
                      <div>
                        <h4 className="font-semibold mb-3">Order Items</h4>
                        <div className="space-y-2">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span>{item.name} × {item.quantity}</span>
                              <span>${(item.wholesalePrice * item.quantity).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="border-t mt-3 pt-3 space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span>Subtotal</span>
                            <span>${(order.subtotal || order.total || 0).toFixed(2)}</span>
                          </div>
                          {order.ccFee && (
                            <div className="flex justify-between text-gray-500">
                              <span>CC Fee</span>
                              <span>${order.ccFee.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-bold">
                            <span>Total</span>
                            <span>${(order.totalWithFee || order.total || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Shipping */}
                      <div>
                        <h4 className="font-semibold mb-3">
                          {order.isDropship ? 'Drop Ship To' : 'Ship To'}
                        </h4>
                        {order.shippingAddress && (
                          <div className="text-sm text-gray-600 mb-4">
                            {order.shippingAddress.name && (
                              <p className="font-medium">{order.shippingAddress.name}</p>
                            )}
                            {order.shippingAddress.company && (
                              <p>{order.shippingAddress.company}</p>
                            )}
                            <p>{order.shippingAddress.street}</p>
                            <p>
                              {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zip}
                            </p>
                          </div>
                        )}

                        {/* Tracking Input */}
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tracking Number
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              defaultValue={order.trackingNumber || ''}
                              placeholder="Enter tracking #"
                              className="flex-1 px-3 py-2 border rounded-lg text-sm"
                              onBlur={(e) => {
                                if (e.target.value !== order.trackingNumber) {
                                  handleTrackingUpdate(order.id, e.target.value);
                                }
                              }}
                            />
                            <button className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                              <Truck size={18} />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Payment & Notes */}
                      <div>
                        <h4 className="font-semibold mb-3">Payment</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Status</span>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              order.paymentStatus === 'succeeded'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {order.paymentStatus === 'succeeded' ? 'Paid' : order.paymentStatus || 'Pending'}
                            </span>
                          </div>
                          {order.stripePaymentId && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Stripe ID</span>
                              <span className="text-gray-600 text-xs">{order.stripePaymentId.slice(-12)}</span>
                            </div>
                          )}
                        </div>

                        {order.notes && (
                          <div className="mt-4">
                            <h4 className="font-semibold mb-2">Order Notes</h4>
                            <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                              {order.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
